import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CompanyListData, Sector, WaterAdministration, CompanyDetail, CompanyUpdateData, Company, CompanyType, Country, District, City, CompanyCreateData, ProductFamilyTree, UserCreateData, CreatedUser, User, UserRole, UserDetail, UserUpdateData, MainSector, Gateway, GatewayCreateData, GatewayUpdateData, Sensor, SensorCreateData, SensorUpdateData, Basin, SubscriptionType, WaterAdministrationMainSector, WaterAdministrationSubSector, CopernicusQueryPayload, ClimateBatchResult, ClimateDatasetListItem } from './clients.types';

export interface ClientsState {
    sectors: {
        data: Sector[];
        loading: boolean;
        error: string | null;
    },
    mainSectors: {
        data: MainSector[];
        loading: boolean;
        error: string | null;
    },
    subscriptionTypes: {
        data: SubscriptionType[];
        loading: boolean;
        error: string | null;
    },
    waterAdministrations: {
        data: WaterAdministration[];
        loading: boolean;
        error: string | null;
    },
    companyLists: {
        data: CompanyListData[];
        loading: boolean;
        error: string | null;
    },
    companyDetail: {
        data: CompanyDetail | null;
        loading: boolean;
        error: string | null;
        success: boolean
    }
    allCompanies: {
        data: Company[] | null;
        loading: boolean;
        error: string | null;
    }
    companyTypes: {
        data: CompanyType[] | null;
        loading: boolean;
        error: string | null;
    }
    countries: {
        data: Country[] | null;
        loading: boolean;
        error: string | null;
    }
    districts: {
        data: District[] | null;
        loading: boolean;
        error: string | null;
    }
    cities: {
        data: City[] | null;
        loading: boolean;
        error: string | null;
    }
    productFamilies: {
        data: ProductFamilyTree[];
        loading: boolean;
        error: string | null;
    }
    userRoles: {
        data: UserRole[];
        loading: boolean;
        error: string | null;
    }
    createUser: {
        loading: boolean;
        error: string | null;
        success: boolean;
    }
    users: {
        data: User[];
        loading: boolean;
        error: string | null;
    }
    userDetail: {
        data: UserDetail | null;
        loading: boolean;
        error: string | null;
        success: boolean;
    }
    gateways: {
        data: Gateway[];
        loading: boolean;
        error: string | null;
        success: boolean;
    }
    sensors: {
        data: Sensor[];
        loading: boolean;
        error: string | null;
        success: boolean;
    }
    basins: {
        data: Basin[];
        loading: boolean;
        error: string | null;
    }
    waterAdministrationMainSectors: {
        data: WaterAdministrationMainSector[];
        loading: boolean;
        error: string | null;
    };

    waterAdministrationSubSectors: {
        data: WaterAdministrationSubSector[];
        loading: boolean;
        error: string | null;
    };

    climateProjections: {
        datasetList: ClimateDatasetListItem[];
        listLoading: boolean;
        error: string | null;
        queryLoading: boolean;
        queryResult: ClimateBatchResult | null;
        queryError: string | null;
    };
}

const initialState: ClientsState = {
    sectors: {
        data: [],
        loading: false,
        error: null,
    },
    mainSectors: {
        data: [],
        loading: false,
        error: null,
    },
    subscriptionTypes: {
        data: [],
        loading: false,
        error: null
    },
    waterAdministrations: {
        data: [],
        loading: false,
        error: null,
    },
    companyLists: {
        data: [],
        loading: false,
        error: null,
    },
    companyDetail: {
        data: null,
        loading: false,
        error: null,
        success: false
    },
    allCompanies: {
        data: [],
        loading: false,
        error: null,
    },
    companyTypes: {
        data: [],
        loading: false,
        error: null,
    },
    countries: {
        data: [],
        loading: false,
        error: null,
    },
    cities: {
        data: [],
        loading: false,
        error: null,
    },
    districts: {
        data: [],
        loading: false,
        error: null,
    },
    productFamilies: {
        data: [],
        loading: false,
        error: null,
    },
    userRoles: {
        data: [],
        loading: false,
        error: null,
    },
    createUser: {
        loading: false,
        error: null,
        success: false,
    },
    users: {
        data: [],
        loading: false,
        error: null,
    },
    userDetail: {
        data: null,
        loading: false,
        error: null,
        success: false,
    },
    gateways: {
        data: [],
        loading: false,
        error: null,
        success: false,
    },
    sensors: {
        data: [],
        loading: false,
        error: null,
        success: false,
    },
    basins: {
        data: [],
        loading: false,
        error: null,
    },
    waterAdministrationMainSectors: {
        data: [],
        loading: false,
        error: null
    },
    waterAdministrationSubSectors: {
        data: [],
        loading: false,
        error: null
    },
    climateProjections: {
        datasetList: [],
        listLoading: false,
        error: null,
        queryLoading: false,
        queryResult: null,
        queryError: null,
    }
};



const clientsSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        fetchSectorsRequest(state) {
            state.sectors.loading = true;
            state.sectors.error = null;
        },
        fetchSectorsSuccess(state, action: PayloadAction<Sector[]>) {
            state.sectors.loading = false;
            state.sectors.data = action.payload;
        },
        fetchSectorsFailure(state, action: PayloadAction<string>) {
            state.sectors.loading = false;
            state.sectors.error = action.payload;
        },
        fetchMainSectorsRequest(state) {
            state.mainSectors.loading = true;
            state.mainSectors.error = null;
        },
        fetchMainSectorsSuccess(state, action: PayloadAction<MainSector[]>) {
            state.mainSectors.loading = false;
            state.mainSectors.data = action.payload;
        },
        fetchMainSectorsFailure(state, action: PayloadAction<string>) {
            state.mainSectors.loading = false;
            state.mainSectors.error = action.payload;
        },

        fetchWaterAdministrationsRequest(state) {
            state.waterAdministrations.loading = true;
            state.waterAdministrations.error = null;
        },
        fetchWaterAdministrationsSuccess(state, action: PayloadAction<WaterAdministration[]>) {
            state.waterAdministrations.loading = false;
            state.waterAdministrations.data = action.payload;
        },
        fetchWaterAdministrationsFailure(state, action: PayloadAction<string>) {
            state.waterAdministrations.loading = false;
            state.waterAdministrations.error = action.payload;
        },
        fetchSubscriptionTypesRequest(state, action: PayloadAction<number>) {
            state.subscriptionTypes.loading = true;
            state.subscriptionTypes.error = null;
        },
        fetchSubscriptionTypesSuccess(state, action: PayloadAction<SubscriptionType[]>) {
            state.subscriptionTypes.loading = false;
            state.subscriptionTypes.data = action.payload;
        },
        fetchSubscriptionTypesFailure(state, action: PayloadAction<string>) {
            state.subscriptionTypes.loading = false;
            state.subscriptionTypes.error = action.payload;
        },

        fetchCompaniesRequest(state) {
            state.companyLists.loading = true;
            state.companyLists.error = null;
        },
        fetchCompaniesSuccess(state, action: PayloadAction<CompanyListData[]>) {
            state.companyLists.loading = false;
            state.companyLists.data = action.payload;
        },
        fetchCompaniesFailure(state, action: PayloadAction<string>) {
            state.companyLists.loading = false;
            state.companyLists.error = action.payload;
        },

        fetchCompanyDetailRequest(state, action: PayloadAction<string>) {
            state.companyDetail.loading = true;
            state.companyDetail.error = null;
        },
        fetchCompanyDetailSuccess(state, action: PayloadAction<CompanyDetail>) {
            state.companyDetail.loading = false;
            state.companyDetail.data = action.payload;
        },
        fetchCompanyDetailFailure(state, action: PayloadAction<string>) {
            state.companyDetail.loading = false;
            state.companyDetail.error = action.payload;
        },

        createCompanyRequest(state, action: PayloadAction<CompanyCreateData>) {
            state.companyDetail.loading = true;
            state.companyDetail.error = null;
            state.companyDetail.success = false;
        },
        createCompanySuccess(state, action: PayloadAction<CompanyDetail>) {
            state.companyDetail.loading = false;
            state.companyDetail.data = action.payload;
            state.companyDetail.success = true;
            state.companyLists.data.unshift({
                id: action.payload.id,
                name: action.payload.name,
                email: action.payload.email,
                isActive: action.payload.isActive,
                createdAt: action.payload.createdAt,
                userCount: 0,
                children: []
            });
        },
        createCompanyFailure(state, action: PayloadAction<string>) {
            state.companyDetail.loading = false;
            state.companyDetail.success = false;
            state.companyDetail.error = action.payload;
        },

        updateCompanyRequest(state, action: PayloadAction<{ id: string; data: CompanyUpdateData }>) {
            state.companyDetail.loading = true;
            state.companyDetail.error = null;
            state.companyDetail.success = false;

        },
        updateCompanySuccess(state, action: PayloadAction<CompanyDetail>) {
            state.companyDetail.loading = false;
            state.companyDetail.data = action.payload;
            state.companyDetail.success = true;
            const index = state.companyLists.data.findIndex(c => c.id === action.payload.id);
            if (index !== -1) {
                state.companyLists.data[index] = {
                    ...state.companyLists.data[index],
                    name: action.payload.name,
                    email: action.payload.email,
                    isActive: action.payload.isActive,
                };
            }
        },
        updateCompanyFailure(state, action: PayloadAction<string>) {
            state.companyDetail.loading = false;
            state.companyDetail.error = action.payload;
            state.companyDetail.success = false;

        },

        clearCompanyDetail(state) {
            state.companyDetail.data = null;
            state.companyDetail.success = false;
            state.companyDetail.error = null;
        },


        fetchAllCompaniesRequest(state) {
            state.allCompanies.loading = true;
            state.allCompanies.error = null;
        },
        fetchAllCompaniesSuccess(state, action: PayloadAction<Company[]>) {
            state.allCompanies.loading = false;
            state.allCompanies.data = action.payload;
        },
        fetchAllCompaniesFailure(state, action: PayloadAction<string>) {
            state.allCompanies.loading = false;
            state.allCompanies.error = action.payload;
        },

        fetchCompanyTypesRequest(state) {
            state.companyTypes.loading = true;
            state.companyTypes.error = null;
        },
        fetchCompanyTypesSuccess(state, action: PayloadAction<CompanyType[]>) {
            state.companyTypes.loading = false;
            state.companyTypes.data = action.payload;
        },
        fetchCompanyTypesFailure(state, action: PayloadAction<string>) {
            state.companyTypes.loading = false;
            state.companyTypes.error = action.payload;
        },

        fetchCitiesRequest(state, action: PayloadAction<number>) {
            state.cities.loading = true;
            state.cities.error = null;
        },

        fetchCitiesSuccess(state, action: PayloadAction<City[]>) {
            state.cities.loading = false;
            state.cities.data = action.payload;
        },
        fetchCitiesFailure(state, action: PayloadAction<string>) {
            state.cities.loading = false;
            state.cities.error = action.payload;
        },

        fetchCountriesRequest(state) {
            state.countries.loading = true;
            state.countries.error = null;
        },

        fetchCountriesSuccess(state, action: PayloadAction<Country[]>) {
            state.countries.loading = false;
            state.countries.data = action.payload;
        },

        fetchCountriesFailure(state, action: PayloadAction<string>) {
            state.countries.loading = false;
            state.countries.error = action.payload;
        },

        fetchDistrictsRequest(state, action: PayloadAction<number>) {
            state.districts.loading = true;
            state.districts.error = null;
        },

        fetchDistrictsSuccess(state, action: PayloadAction<District[]>) {
            state.districts.loading = false;
            state.districts.data = action.payload;
        },
        fetchDistrictsFailure(state, action: PayloadAction<string>) {
            state.districts.loading = false;
            state.districts.error = action.payload;
        },

        fetchProductFamiliesRequest(state) {
            state.productFamilies.loading = true;
            state.productFamilies.error = null;
        },
        fetchProductFamiliesSuccess(state, action: PayloadAction<ProductFamilyTree[]>) {
            state.productFamilies.loading = false;
            state.productFamilies.data = action.payload;
        },
        fetchProductFamiliesFailure(state, action: PayloadAction<string>) {
            state.productFamilies.loading = false;
            state.productFamilies.error = action.payload;
        },
        // --- USER ROLE ACTIONS ---
        fetchUserRolesRequest(state) {
            state.userRoles.loading = true;
            state.userRoles.error = null;
        },
        fetchUserRolesSuccess(state, action: PayloadAction<UserRole[]>) {
            state.userRoles.loading = false;
            state.userRoles.data = action.payload;
        },
        fetchUserRolesFailure(state, action: PayloadAction<string>) {
            state.userRoles.loading = false;
            state.userRoles.error = action.payload;
        },

        // --- CREATE USER ACTIONS ---
        createUserRequest(state, action: PayloadAction<{ companyId: string; data: UserCreateData }>) {
            state.createUser.loading = true;
            state.createUser.error = null;
            state.createUser.success = false;
        },
        createUserSuccess(state, action: PayloadAction<CreatedUser>) {
            state.createUser.loading = false;
            state.createUser.success = true;
            const companyIndex = state.companyLists.data.findIndex(c => c.id === action.payload.companyId);
            if (companyIndex !== -1) {
                state.companyLists.data[companyIndex].userCount++;
            }
        },
        createUserFailure(state, action: PayloadAction<string>) {
            state.createUser.loading = false;
            state.createUser.error = action.payload;
            state.createUser.success = false;
        },
        clearCreateUserStatus(state) {
            state.createUser.loading = false;
            state.createUser.error = null;
            state.createUser.success = false;
        },

        // --- FETCH ALL USERS ACTIONS ---
        fetchUsersRequest(state, action: PayloadAction<string>) {
            state.users.loading = true;
            state.users.error = null;
        },
        fetchUsersSuccess(state, action: PayloadAction<User[]>) {
            state.users.loading = false;
            state.users.data = action.payload;
        },
        fetchUsersFailure(state, action: PayloadAction<string>) {
            state.users.loading = false;
            state.users.error = action.payload;
        },
        fetchUserDetailRequest(state, action: PayloadAction<string>) {
            state.userDetail.loading = true;
            state.userDetail.error = null;
        },
        fetchUserDetailSuccess(state, action: PayloadAction<UserDetail>) {
            state.userDetail.loading = false;
            state.userDetail.data = action.payload;
        },
        fetchUserDetailFailure(state, action: PayloadAction<string>) {
            state.userDetail.loading = false;
            state.userDetail.error = action.payload;
        },
        updateUserRequest(state, action: PayloadAction<{ userId: string; data: UserUpdateData }>) {
            state.userDetail.loading = true;
            state.userDetail.error = null;
            state.userDetail.success = false;
        },
        updateUserSuccess(state, action: PayloadAction<UserDetail>) {
            state.userDetail.loading = false;
            state.userDetail.success = true;
            state.userDetail.data = action.payload;
            // Update the user in the main users list if it exists
            const index = state.users.data.findIndex(u => u.id === action.payload.id);
            if (index !== -1) {
                state.users.data[index] = { ...state.users.data[index], ...action.payload };
            }
        },
        updateUserFailure(state, action: PayloadAction<string>) {
            state.userDetail.loading = false;
            state.userDetail.error = action.payload;
            state.userDetail.success = false;
        },

        // --- CLEAR USER DETAIL ---
        clearUserDetail(state) {
            state.userDetail.data = null;
            state.userDetail.loading = false;
            state.userDetail.error = null;
            state.userDetail.success = false;
        },

        fetchGatewaysRequest(state, action: PayloadAction<string>) {
            state.gateways.loading = true;
            state.gateways.error = null;
        },
        fetchGatewaysSuccess(state, action: PayloadAction<Gateway[]>) {
            state.gateways.loading = false;
            state.gateways.data = action.payload;
        },
        fetchGatewaysFailure(state, action: PayloadAction<string>) {
            state.gateways.loading = false;
            state.gateways.error = action.payload;
        },

        createGatewayRequest(state, action: PayloadAction<{ companyId: string; data: GatewayCreateData }>) {
            state.gateways.loading = true;
            state.gateways.error = null;
            state.gateways.success = false;
        },
        createGatewaySuccess(state, action: PayloadAction<Gateway>) {
            state.gateways.loading = false;
            state.gateways.success = true;
        },
        createGatewayFailure(state, action: PayloadAction<string>) {
            state.gateways.loading = false;
            state.gateways.error = action.payload;
            state.gateways.success = false;
        },

        updateGatewayRequest(state, action: PayloadAction<{ gatewayId: string; data: GatewayUpdateData; clientId: string }>) {
            state.gateways.loading = true;
            state.gateways.error = null;
            state.gateways.success = false;
        },
        updateGatewaySuccess(state, action: PayloadAction<Gateway>) {
            state.gateways.loading = false;
            state.gateways.success = true;
            const index = state.gateways.data.findIndex(g => g.id === action.payload.id);
            if (index !== -1) {
                state.gateways.data[index] = action.payload;
            }
        },
        updateGatewayFailure(state, action: PayloadAction<string>) {
            state.gateways.loading = false;
            state.gateways.error = action.payload;
            state.gateways.success = false;
        },

        deleteGatewayRequest(state, action: PayloadAction<{ gatewayId: string; clientId: string }>) {
            state.gateways.loading = true;
            state.gateways.error = null;
            state.gateways.success = false;
        },
        deleteGatewaySuccess(state, action: PayloadAction<string>) {
            state.gateways.loading = false;
            state.gateways.success = true;
            state.gateways.data = state.gateways.data.filter(g => g.id !== action.payload);
        },
        deleteGatewayFailure(state, action: PayloadAction<string>) {
            state.gateways.loading = false;
            state.gateways.error = action.payload;
            state.gateways.success = false;
        },

        clearGatewayStatus(state) {
            state.gateways.error = null;
            state.gateways.success = false;
        },

        fetchSensorsRequest(state, action: PayloadAction<string>) {
            state.sensors.loading = true;
            state.sensors.error = null;
        },
        fetchSensorsSuccess(state, action: PayloadAction<Sensor[]>) {
            state.sensors.loading = false;
            state.sensors.data = action.payload;
        },
        fetchSensorsFailure(state, action: PayloadAction<string>) {
            state.sensors.loading = false;
            state.sensors.error = action.payload;
        },

        createSensorRequest(state, action: PayloadAction<SensorCreateData>) {
            state.sensors.loading = true;
            state.sensors.error = null;
            state.sensors.success = false;
        },
        createSensorSuccess(state, action: PayloadAction<Sensor>) {
            state.sensors.loading = false;
            state.sensors.success = true;
        },
        createSensorFailure(state, action: PayloadAction<string>) {
            state.sensors.loading = false;
            state.sensors.error = action.payload;
        },
        updateSensorRequest(state, action: PayloadAction<{ sensorId: string; data: SensorUpdateData }>) {
            state.sensors.loading = true;
            state.sensors.error = null;
            state.sensors.success = false;
        },
        updateSensorSuccess(state, action: PayloadAction<Sensor>) {
            state.sensors.loading = false;
            state.sensors.success = true;
            const index = state.sensors.data.findIndex(s => s.id === action.payload.id);
            if (index !== -1) {
                state.sensors.data[index] = action.payload;
            }
        },
        updateSensorFailure(state, action: PayloadAction<string>) {
            state.sensors.loading = false;
            state.sensors.error = action.payload;
            state.sensors.success = false;
        },

        deleteSensorRequest(state, action: PayloadAction<{ sensorId: string }>) {
            state.sensors.loading = true;
            state.sensors.error = null;
            state.sensors.success = false;
        },
        deleteSensorSuccess(state, action: PayloadAction<string>) {
            state.sensors.loading = false;
            state.sensors.success = true;
            state.sensors.data = state.sensors.data.filter(s => s.id !== action.payload);
        },
        deleteSensorFailure(state, action: PayloadAction<string>) {
            state.sensors.loading = false;
            state.sensors.error = action.payload;
            state.sensors.success = false;
        },

        clearSensorStatus(state) {
            state.sensors.error = null;
            state.sensors.success = false;
        },

        fetchBasinsRequest(state) {
            state.basins.loading = true;
            state.basins.error = null;
        },
        fetchBasinsSuccess(state, action: PayloadAction<Basin[]>) {
            state.basins.loading = false;
            state.basins.data = action.payload;
        },
        fetchBasinsFailure(state, action: PayloadAction<string>) {
            state.basins.loading = false;
            state.basins.error = action.payload;
        },

        fetchWaterAdministrationMainSectorsRequest: (state, action: PayloadAction<number>) => {
            state.waterAdministrationMainSectors.loading = true;
            state.waterAdministrationMainSectors.error = null;

            state.waterAdministrationMainSectors.data = [];
            state.waterAdministrationSubSectors.data = [];
        },
        fetchWaterAdministrationMainSectorsSuccess: (state, action: PayloadAction<WaterAdministrationMainSector[]>) => {
            state.waterAdministrationMainSectors.loading = false;
            state.waterAdministrationMainSectors.data = action.payload;
        },
        fetchWaterAdministrationMainSectorsFailure: (state, action: PayloadAction<string>) => {
            state.waterAdministrationMainSectors.loading = false;
            state.waterAdministrationMainSectors.error = action.payload;
        },

        fetchWaterAdministrationSubSectorsRequest: (state, action: PayloadAction<number>) => {
            state.waterAdministrationSubSectors.loading = true;
            state.waterAdministrationSubSectors.error = null;
            state.waterAdministrationSubSectors.data = [];
        },
        fetchWaterAdministrationSubSectorsSuccess: (state, action: PayloadAction<WaterAdministrationSubSector[]>) => {
            state.waterAdministrationSubSectors.loading = false;
            state.waterAdministrationSubSectors.data = action.payload;
        },
        fetchWaterAdministrationSubSectorsFailure: (state, action: PayloadAction<string>) => {
            state.waterAdministrationSubSectors.loading = false;
            state.waterAdministrationSubSectors.error = action.payload;
        },

        clearWaterAdministrationSubSectors: (state) => {
            state.waterAdministrationSubSectors.data = [];
            state.waterAdministrationSubSectors.loading = false;
            state.waterAdministrationSubSectors.error = null;
        },

        resetWaterAdministrationSectorState: (state) => {
            state.waterAdministrationMainSectors = { data: [], loading: false, error: null };
            state.waterAdministrationSubSectors = { data: [], loading: false, error: null };
        },
        fetchDatasetListStart(state) {
            state.climateProjections.listLoading = true;
            state.climateProjections.error = null;
        },
        fetchDatasetListSuccess(state, action: PayloadAction<ClimateDatasetListItem[]>) {
            state.climateProjections.listLoading = false;
            state.climateProjections.datasetList = action.payload;
        },
        fetchDatasetListFailure(state, action: PayloadAction<string>) {
            state.climateProjections.listLoading = false;
            state.climateProjections.error = action.payload;
        },
        startCopernicusQuery(state, action: PayloadAction<CopernicusQueryPayload>) {
            state.climateProjections.queryLoading = true;
            state.climateProjections.queryError = null;
            state.climateProjections.queryResult = null;
        },
        copernicusQuerySuccess(state, action: PayloadAction<ClimateBatchResult>) {
            state.climateProjections.queryLoading = false;
            state.climateProjections.queryResult = action.payload;
        },
        copernicusQueryFailure(state, action: PayloadAction<string>) {
            state.climateProjections.queryLoading = false;
            state.climateProjections.queryError = action.payload;
        },
        clearQueryStatus(state) {
            state.climateProjections.queryLoading = false;
            state.climateProjections.queryError = null;
            state.climateProjections.queryResult = null;
        }
    },
});

export const {
    // Sector actions
    fetchSectorsRequest,
    fetchSectorsSuccess,
    fetchSectorsFailure,

    fetchMainSectorsRequest,
    fetchMainSectorsSuccess,
    fetchMainSectorsFailure,

    // Water Administration actions
    fetchWaterAdministrationsRequest,
    fetchWaterAdministrationsSuccess,
    fetchWaterAdministrationsFailure,

    // Companies actions
    fetchCompaniesRequest,
    fetchCompaniesSuccess,
    fetchCompaniesFailure,

    // Company detail actions
    fetchCompanyDetailRequest,
    fetchCompanyDetailSuccess,
    fetchCompanyDetailFailure,

    // Create company actions
    createCompanyRequest,
    createCompanySuccess,
    createCompanyFailure,

    // Update company actions
    updateCompanyRequest,
    updateCompanySuccess,
    updateCompanyFailure,

    // All Companies actions
    fetchAllCompaniesRequest,
    fetchAllCompaniesSuccess,
    fetchAllCompaniesFailure,

    // Company Types actions
    fetchCompanyTypesRequest,
    fetchCompanyTypesSuccess,
    fetchCompanyTypesFailure,

    // Cities  actions
    fetchCitiesRequest,
    fetchCitiesSuccess,
    fetchCitiesFailure,

    // Countries  actions
    fetchCountriesRequest,
    fetchCountriesSuccess,
    fetchCountriesFailure,

    // Districts  actions
    fetchDistrictsRequest,
    fetchDistrictsSuccess,
    fetchDistrictsFailure,

    fetchProductFamiliesRequest,
    fetchProductFamiliesSuccess,
    fetchProductFamiliesFailure,

    // Clear action
    clearCompanyDetail,

    // User actions
    fetchUserRolesRequest,
    fetchUserRolesSuccess,
    fetchUserRolesFailure,
    createUserRequest,
    createUserSuccess,
    createUserFailure,
    clearCreateUserStatus,
    fetchUsersRequest,
    fetchUsersSuccess,
    fetchUsersFailure,
    fetchUserDetailRequest,
    fetchUserDetailSuccess,
    fetchUserDetailFailure,
    updateUserRequest,
    updateUserSuccess,
    updateUserFailure,
    clearUserDetail,

    // --- GATEWAY ACTION EXPORTS ---
    fetchGatewaysRequest,
    fetchGatewaysSuccess,
    fetchGatewaysFailure,
    createGatewayRequest,
    createGatewaySuccess,
    createGatewayFailure,
    updateGatewayRequest,
    updateGatewaySuccess,
    updateGatewayFailure,
    deleteGatewayRequest,
    deleteGatewaySuccess,
    deleteGatewayFailure,
    clearGatewayStatus,

    // --- SENSOR ACTION EXPORTS ---
    fetchSensorsRequest,
    fetchSensorsSuccess,
    fetchSensorsFailure,
    createSensorRequest,
    createSensorSuccess,
    createSensorFailure,
    updateSensorRequest,
    updateSensorSuccess,
    updateSensorFailure,
    deleteSensorRequest,
    deleteSensorSuccess,
    deleteSensorFailure,
    clearSensorStatus,

    fetchBasinsRequest,
    fetchBasinsSuccess,
    fetchBasinsFailure,

    fetchSubscriptionTypesRequest,
    fetchSubscriptionTypesSuccess,
    fetchSubscriptionTypesFailure,

    fetchWaterAdministrationMainSectorsRequest,
    fetchWaterAdministrationMainSectorsSuccess,
    fetchWaterAdministrationMainSectorsFailure,
    fetchWaterAdministrationSubSectorsRequest,
    fetchWaterAdministrationSubSectorsSuccess,
    fetchWaterAdministrationSubSectorsFailure,
    clearWaterAdministrationSubSectors,
    resetWaterAdministrationSectorState,

    fetchDatasetListStart,
    fetchDatasetListSuccess,
    fetchDatasetListFailure,
    startCopernicusQuery,
    copernicusQuerySuccess,
    copernicusQueryFailure,
    clearQueryStatus



} = clientsSlice.actions;

export default clientsSlice.reducer;